import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { startTransition, useCallback, useEffect, useRef } from "react";
import { useCart } from "@/cart";
import { CartType } from "@cappelendamm/e-commerce-services";
import { useAuth } from "@clerk/nextjs";
import { getTargetCartType } from "@/cart/utils/getTargetCartType";
import { UNINITIALIZED_CART } from "@/cart/initial-cart";

/**
 * Handles both cart type swapping (navigation-based) and adding items from
 * query parameters (`?cart-add-item=SKU&cart-type=TYPE`).
 *
 * These two concerns are combined into a single component to eliminate a race
 * condition where the swap effect and the add-from-query-param effect could
 * fire independently and interleave their dispatched actions.
 *
 * Must be rendered inside a Suspense boundary (uses `useSearchParams`).
 */
export function CartSwapAndQueryParamHandler() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const { orgId, isLoaded, sessionClaims } = useAuth();
  const { cart, addItem, swapCart, isPending: cartIsPending } = useCart();
  const activeSchool = (sessionClaims as CustomJwtSessionClaims | undefined)
    ?.publicMeta?.activeSchool;

  const isProcessingRef = useRef(false);
  const lastSwappedToRef = useRef<CartType | null>(null);

  const removeQueryParams = useCallback(() => {
    const queryParams = new URLSearchParams(searchParams);
    queryParams.delete("cart-add-item");
    queryParams.delete("cart-type");
    const qs = queryParams.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname);
  }, [pathname, router, searchParams]);

  // Reset swap tracker on navigation or org change
  useEffect(() => {
    lastSwappedToRef.current = null;
  }, [pathname, orgId, activeSchool]);

  useEffect(() => {
    if (!isLoaded || cartIsPending || isProcessingRef.current) return;
    if (!cart?.cartType || !cart.id || cart.id === UNINITIALIZED_CART.id)
      return;

    const skus = searchParams.getAll("cart-add-item");
    const queryCartType = searchParams.get("cart-type");
    const hasQueryParamItems = skus.length > 0;

    const targetType =
      hasQueryParamItems && queryCartType
        ? (queryCartType as CartType)
        : getTargetCartType(pathname, orgId, activeSchool);

    const needsSwap = cart.cartType !== targetType;

    if (hasQueryParamItems) {
      const existingSkus = new Set(cart.items.map((item) => item.variant.sku));
      const itemsToAdd = skus.filter((sku) => !existingSkus.has(sku));

      if (itemsToAdd.length === 0) {
        removeQueryParams();
        return;
      }

      isProcessingRef.current = true;
      startTransition(async () => {
        if (needsSwap) {
          await swapCart({ targetCartType: targetType, skus: itemsToAdd });
        } else {
          for (const sku of itemsToAdd) {
            await addItem({ sku });
          }
        }
        removeQueryParams();
        isProcessingRef.current = false;
      });
    } else if (needsSwap && lastSwappedToRef.current !== targetType) {
      isProcessingRef.current = true;
      lastSwappedToRef.current = targetType;

      startTransition(async () => {
        await swapCart({ targetCartType: targetType });
        isProcessingRef.current = false;
      });
    }
  }, [
    activeSchool,
    addItem,
    cart,
    cartIsPending,
    isLoaded,
    orgId,
    pathname,
    removeQueryParams,
    searchParams,
    swapCart,
  ]);

  return null;
}
